<script>
  import { tradeState, isFinalized } from '../../lib/stores/trade.svelte.js';
  import { acceptTrade } from '../../lib/services/trade-service.js';

  let { onCancel } = $props();

  // Swipe-to-confirm state
  let trackEl = $state(null);
  let thumbEl = $state(null);
  let dragging = $state(false);
  let progress = $state(0); // 0 to 1
  let confirmed = $state(false);
  let accepting = $state(false);

  const CONFIRM_THRESHOLD = 0.85;

  const trade = $derived(tradeState.currentTrade);
  const userSide = $derived(tradeState.userSide);
  const finalized = $derived(isFinalized());

  const userAccepted = $derived.by(() => {
    if (!trade || !userSide) return false;
    return userSide === 'A' ? trade.acceptedByA : trade.acceptedByB;
  });

  const otherAccepted = $derived.by(() => {
    if (!trade || !userSide) return false;
    return userSide === 'A' ? trade.acceptedByB : trade.acceptedByA;
  });

  // Reset local state when acceptance is revoked (trade was modified)
  $effect(() => {
    if (!userAccepted && confirmed) {
      confirmed = false;
      progress = 0;
    }
  });

  // Touch handling for swipe
  let startX = 0;
  let trackWidth = 0;
  let thumbWidth = 0;

  function onTouchStart(e) {
    if (confirmed || userAccepted || finalized || accepting) return;
    if (e.touches.length !== 1) return;

    const touch = e.touches[0];
    startX = touch.clientX;
    trackWidth = trackEl.offsetWidth;
    thumbWidth = thumbEl.offsetWidth;
    dragging = true;
    progress = 0;
  }

  function onTouchMove(e) {
    if (!dragging) return;
    e.preventDefault();

    const touch = e.touches[0];
    const deltaX = touch.clientX - startX;
    const maxTravel = trackWidth - thumbWidth;
    progress = Math.max(0, Math.min(1, deltaX / maxTravel));

    // Haptic feedback at threshold
    if (progress >= CONFIRM_THRESHOLD && navigator.vibrate) {
      navigator.vibrate(10);
    }
  }

  function onTouchEnd() {
    if (!dragging) return;
    dragging = false;

    if (progress >= CONFIRM_THRESHOLD) {
      confirmed = true;
      progress = 1;
      if (navigator.vibrate) {
        navigator.vibrate([30, 50, 60]);
      }
      doAccept();
    } else {
      // Snap back
      progress = 0;
    }
  }

  // Mouse handling for desktop
  let mouseDown = false;

  function onMouseDown(e) {
    if (confirmed || userAccepted || finalized || accepting) return;

    startX = e.clientX;
    trackWidth = trackEl.offsetWidth;
    thumbWidth = thumbEl.offsetWidth;
    mouseDown = true;
    dragging = true;
    progress = 0;

    // Add window listeners for mouse
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  function onMouseMove(e) {
    if (!mouseDown) return;

    const deltaX = e.clientX - startX;
    const maxTravel = trackWidth - thumbWidth;
    progress = Math.max(0, Math.min(1, deltaX / maxTravel));
  }

  function onMouseUp() {
    if (!mouseDown) return;
    mouseDown = false;
    dragging = false;

    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);

    if (progress >= CONFIRM_THRESHOLD) {
      confirmed = true;
      progress = 1;
      doAccept();
    } else {
      progress = 0;
    }
  }

  async function doAccept() {
    accepting = true;
    try {
      await acceptTrade(tradeState.currentTradeId, userSide);
    } catch (err) {
      console.error('[Trade] Accept failed:', err);
      confirmed = false;
      progress = 0;
    } finally {
      accepting = false;
    }
  }

  // Computed thumb position
  const thumbTranslateX = $derived.by(() => {
    if (!trackEl || !thumbEl) return 0;
    const maxTravel = (trackEl?.offsetWidth || 200) - (thumbEl?.offsetWidth || 48);
    return progress * maxTravel;
  });
</script>

<div class="accept-area">
  <div class="accept-slider">
    {#if finalized}
      <div class="status-badge finalized">
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg
        >
        Trade Finalized
      </div>
    {:else if userAccepted}
      <div class="status-badge accepted">
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg
        >
        Accepted
        {#if !otherAccepted}
          <span class="waiting">— waiting for other</span>
        {/if}
      </div>
    {:else}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        class="swipe-track"
        class:dragging
        class:confirmed
        bind:this={trackEl}
        ontouchstart={onTouchStart}
        ontouchmove={onTouchMove}
        ontouchend={onTouchEnd}
      >
        <div
          class="swipe-fill"
          style:width="{progress * 100}%"
          style:transition={dragging ? 'none' : 'width 0.3s ease-out'}
        ></div>
        <span class="swipe-label" style:opacity={1 - progress * 1.5}> Slide to Accept </span>
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="swipe-thumb"
          bind:this={thumbEl}
          style:transform="translateX({thumbTranslateX}px)"
          style:transition={dragging ? 'none' : 'transform 0.3s ease-out'}
          onmousedown={onMouseDown}
        >
          {#if confirmed}
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="white"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg
            >
          {:else}
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="white"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg
            >
          {/if}
        </div>
      </div>
    {/if}
  </div>

  {#if !finalized}
    <button class="cancel-btn" onclick={onCancel} title="Cancel Trade">
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        ><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg
      >
    </button>
  {/if}
</div>

<style>
  .accept-area {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  .accept-slider {
    width: 100%;
    max-width: 280px;
    height: 44px;
    display: flex;
    align-items: center;
  }

  /* Swipe track */
  .swipe-track {
    position: relative;
    height: 44px;
    border-radius: 22px;
    background: #e5e7eb;
    overflow: hidden;
    flex: 1;
    touch-action: none;
    user-select: none;
    cursor: grab;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.12);
  }

  .swipe-track.dragging {
    cursor: grabbing;
  }

  .swipe-track.confirmed {
    background: #22c55e;
  }

  /* Green fill that follows thumb */
  .swipe-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
    border-radius: 22px;
    pointer-events: none;
  }

  /* Label text */
  .swipe-label {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 600;
    color: #6b7280;
    pointer-events: none;
    transition: opacity 0.1s;
  }

  /* Draggable thumb */
  .swipe-thumb {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    z-index: 2;
    cursor: grab;
  }

  .swipe-track.dragging .swipe-thumb {
    cursor: grabbing;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
  }

  .swipe-track.confirmed .swipe-thumb {
    background: #16a34a;
  }

  /* Status badges */
  .status-badge {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.5rem 1rem;
    border-radius: 9999px;
    font-size: 0.8rem;
    font-weight: 600;
    flex: 1;
    justify-content: center;
  }

  .status-badge.accepted {
    background: #dcfce7;
    color: #16a34a;
  }

  .status-badge.finalized {
    background: #f3f4f6;
    color: #6b7280;
  }

  .waiting {
    font-weight: 400;
    font-size: 0.75rem;
    color: #9ca3af;
  }

  /* Cancel button — absolutely positioned to far right */
  .cancel-btn {
    position: absolute;
    right: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: #fee2e2;
    color: #ef4444;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
  }

  .cancel-btn:hover {
    background: #fecaca;
  }

  @media (max-width: 768px) {
    .accept-slider {
      max-width: 240px;
      height: 40px;
    }

    .swipe-track {
      height: 40px;
    }

    .swipe-thumb {
      width: 34px;
      height: 34px;
    }

    .cancel-btn {
      width: 32px;
      height: 32px;
    }
  }
</style>
